import React, { useState, useEffect } from 'react';
import { 
  Server, Database, Shield, Package, GitBranch, 
  CheckCircle, XCircle, AlertTriangle, RefreshCw, Download
} from 'lucide-react';

import axios from 'axios';
import { getUser, isPlatformAdmin, isAnyAdmin } from '../lib/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const SystemHealthPage = () => {
  // Restrict to super admins only
  const user = getUser();
  const isSuperAdmin = isPlatformAdmin();
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Shield className="w-8 h-8 text-red-600 mr-3" />
        <span className="text-xl text-gray-700">Access restricted: Super admin only</span>
      </div>
    );
  }
  const [healthData, setHealthData] = useState(null);
  const [dockerData, setDockerData] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState(null);
  const [temporalData, setTemporalData] = useState(null);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [health, docker, vulns, vers, temporal] = await Promise.all([
        axios.get(`/api/v1/admin/health`),
        axios.get(`/api/v1/admin/docker`),
        axios.get(`/api/v1/admin/vulnerabilities`),
        axios.get(`/api/v1/admin/versions?repo_url=https://github.com/yourusername/online-mcc-v1`),
        axios.get(`/api/v1/temporal/queue-status`)
      ]);

      setHealthData(health.data.data);
      setDockerData(docker.data.data);
      setVulnerabilities(vulns.data.data);
      setVersions(vers.data.data);
      setTemporalData(temporal.data);
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  const restartContainer = async (containerName) => {
    if (!confirm(`Are you sure you want to restart ${containerName}?`)) return;
    
    try {
      await axios.post(`/api/v1/admin/restart-container`, {
        container_name: containerName,
        admin_user: 'admin'
      });
      alert(`Container ${containerName} restarted successfully`);
      fetchAllData();
    } catch (error) {
      alert(`Error restarting container: ${error.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      healthy: 'bg-green-100 text-green-800',
      running: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800',
      stopped: 'bg-red-100 text-red-800',
      error: 'bg-red-100 text-red-800',
      unavailable: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || badges.unavailable;
  };

  // Helper: filter out one-off/expected-to-exit containers
  const isOneOffContainer = (name) => {
    // Regex for common one-off/ephemeral container patterns
    return /(_init|_runner|_migration|_job|_oneoff|_test|_seed|_setup|_backup|_tmp|_cleanup|_cron|_once|_exit|\.1|\.2|\.3|\.bak|\.tmp)$/i.test(name);
  };

  // Helper: filter out non-critical containers from critical alerts
  const filterCriticalContainers = (containers) => {
    if (!Array.isArray(containers)) return [];
    return containers.filter(
      (container) =>
        container.status === 'critical' &&
        !isOneOffContainer(container.name)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">System Health Monitor</h1>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            {['overview', 'temporal', 'temporal-queue', 'docker', 'vulnerabilities', 'versions'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium capitalize ${tab === 'temporal-queue' ? 'Temporal Queue' : tab} ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'temporal-queue' ? 'Temporal Queue' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && healthData && (
          <div className="space-y-6">
            {/* System Resources */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Server className="w-6 h-6 text-blue-600" />
                  System Resources
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-sm text-gray-600 mb-2">CPU Usage</div>
                  <div className="text-3xl font-bold mb-2">
                    {healthData.system?.cpu_percent?.toFixed(1)}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${healthData.system?.cpu_percent}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Memory Usage</div>
                  <div className="text-3xl font-bold mb-2">
                    {healthData.system?.memory_percent?.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    {healthData.system?.memory_available_gb} GB available
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Disk Usage</div>
                  <div className="text-3xl font-bold mb-2">
                    {healthData.system?.disk_percent?.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-500">
                    {healthData.system?.disk_free_gb} GB free
                  </div>
                </div>
              </div>
            </div>

            {/* Database & Redis */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Database className="w-6 h-6 text-purple-600" />
                    Database
                  </h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(healthData.database?.status)}`}>
                      {healthData.database?.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Size</span>
                      <span className="font-semibold">{healthData.database?.size_mb} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Connections</span>
                      <span className="font-semibold">{healthData.database?.active_connections}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Host</span>
                      <span className="font-semibold">{healthData.database?.host}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Database className="w-6 h-6 text-red-600" />
                    Redis
                  </h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(healthData.redis?.status)}`}>
                      {healthData.redis?.status}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Version</span>
                      <span className="font-semibold">{healthData.redis?.version}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Memory</span>
                      <span className="font-semibold">{healthData.redis?.used_memory_mb} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Clients</span>
                      <span className="font-semibold">{healthData.redis?.connected_clients}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Temporal Queue Tab */}
        {activeTab === 'temporal' && (
          <div className="space-y-6">
            {/* Temporal Workflow Status */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <GitBranch className="w-6 h-6 text-purple-600" />
                  Temporal Workflow Queue
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Real-time status of Temporal workflows and task queues
                </p>
              </div>
              <div className="p-6">
                {temporalData ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">{temporalData.active_workflows || 0}</div>
                        <p className="text-sm text-gray-600">Active Workflows</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                        <div className="text-2xl font-bold text-green-600">{temporalData.completed_today || 0}</div>
                        <p className="text-sm text-gray-600">Completed Today</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                        <div className="text-2xl font-bold text-yellow-600">{temporalData.pending_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Pending Tasks</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
                        <div className="text-2xl font-bold text-red-600">{temporalData.failed_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Failed Tasks</p>
                      </div>
                    </div>

                    {/* Task Queues */}
                    {temporalData.queues && temporalData.queues.length > 0 ? (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Task Queues</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {temporalData.queues.map((queue, index) => (
                            <div key={index} className="bg-white border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900">{queue.name || `Queue ${index + 1}`}</h4>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(queue.status || 'active')}`}>
                                  {queue.status || 'Active'}
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Active Tasks:</span>
                                  <span className="font-semibold text-blue-600">{queue.active_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Pending Tasks:</span>
                                  <span className="font-semibold text-yellow-600">{queue.pending_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Avg Processing Time:</span>
                                  <span className="font-semibold">{queue.avg_processing_time || 'N/A'}</span>
                                </div>
                                {queue.last_activity && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Last Activity:</span>
                                    <span className="font-semibold text-xs">{new Date(queue.last_activity).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Task Queues</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Default Queues when no data */}
                          <div className="bg-white border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900">Merchant Processing</h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900">MCC Classification</h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recent Workflow Activity */}
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4">Recent Workflow Activity</h3>
                      {temporalData.recent_workflows && temporalData.recent_workflows.length > 0 ? (
                        <div className="space-y-2">
                          {temporalData.recent_workflows.map((workflow, index) => (
                            <div key={index} className="bg-white border rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold">{workflow.workflow_id}</div>
                                  <div className="text-sm text-gray-600">{workflow.workflow_type}</div>
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(workflow.status)}`}>
                                    {workflow.status}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(workflow.start_time).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                          <GitBranch className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p>No recent workflow activity</p>
                          <p className="text-sm">Workflow data will appear here when Temporal is active</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading Temporal queue status...</p>
                  </div>
                )}

                {/* Temporal Server Status */}
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-purple-900">Temporal Server Status</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Distributed workflow orchestration system
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        temporalData?.server_status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {temporalData?.server_status === 'connected' ? 'Connected' : 'Not Connected'}
                      </span>
                      <button
                        onClick={fetchAllData}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-purple-700">Server URL:</span>
                      <span className="font-mono">{temporalData?.server_url || 'localhost:7233'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">Namespace:</span>
                      <span className="font-mono">{temporalData?.namespace || 'default'}</span>
                    </div>
                    {temporalData?.last_updated && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-purple-700">Last Updated:</span>
                        <span className="font-mono text-xs">{new Date(temporalData.last_updated).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Temporal Queue Tab - Enhanced Real-time Monitoring */}
        {activeTab === 'temporal-queue' && (
          <div className="space-y-6">
            {/* Real-time Queue Status */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <GitBranch className="w-6 h-6 text-purple-600" />
                  Temporal Queue Monitor
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Real-time workflow monitoring with batch status by time periods
                </p>
              </div>
              <div className="p-6">
                {temporalData ? (
                  <>
                    {/* Real-time Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">{temporalData.active_workflows || 0}</div>
                        <p className="text-sm text-gray-600">Active Workflows</p>
                        <p className="text-xs text-blue-500 mt-1">Real-time</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                        <div className="text-2xl font-bold text-green-600">{temporalData.completed_today || 0}</div>
                        <p className="text-sm text-gray-600">Completed Today</p>
                        <p className="text-xs text-green-500 mt-1">Last 24h</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                        <div className="text-2xl font-bold text-yellow-600">{temporalData.pending_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Pending Tasks</p>
                        <p className="text-xs text-yellow-500 mt-1">Queued</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
                        <div className="text-2xl font-bold text-red-600">{temporalData.failed_tasks || 0}</div>
                        <p className="text-sm text-gray-600">Failed Tasks</p>
                        <p className="text-xs text-red-500 mt-1">Errors</p>
                      </div>
                    </div>

                    {/* Upload Batches by Time Period */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Package className="w-5 h-5 text-purple-600" />
                        Upload Batches by Time Period
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { period: 'Daily', count: temporalData.daily_batches || 0, color: 'blue' },
                          { period: 'Weekly', count: temporalData.weekly_batches || 0, color: 'green' },
                          { period: 'Monthly', count: temporalData.monthly_batches || 0, color: 'purple' },
                          { period: 'Quarterly', count: temporalData.quarterly_batches || 0, color: 'orange' }
                        ].map((batch) => (
                          <div key={batch.period} className={`text-center p-4 bg-${batch.color}-50 rounded-lg border-2 border-${batch.color}-200`}>
                            <div className={`text-2xl font-bold text-${batch.color}-600`}>{batch.count}</div>
                            <p className="text-sm text-gray-600">{batch.period}</p>
                            <p className="text-xs text-gray-500 mt-1">Batches</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Enhanced Task Queues with Real-time Status */}
                    {temporalData.queues && temporalData.queues.length > 0 ? (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Server className="w-5 h-5 text-indigo-600" />
                          Active Task Queues
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {temporalData.queues.map((queue, index) => (
                            <div key={index} className="bg-gradient-to-br from-white to-gray-50 border rounded-lg p-4 shadow-sm">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${queue.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                                  {queue.name || `Queue ${index + 1}`}
                                </h4>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(queue.status || 'active')}`}>
                                  {queue.status || 'Active'}
                                </span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Active Tasks:</span>
                                  <span className="font-semibold text-blue-600">{queue.active_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Pending Tasks:</span>
                                  <span className="font-semibold text-yellow-600">{queue.pending_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Completed Tasks:</span>
                                  <span className="font-semibold text-green-600">{queue.completed_tasks || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Avg Processing Time:</span>
                                  <span className="font-semibold">{queue.avg_processing_time || 'N/A'}</span>
                                </div>
                                {queue.last_activity && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Last Activity:</span>
                                    <span className="font-semibold text-xs">{new Date(queue.last_activity).toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                              {/* Progress Bar */}
                              {queue.active_tasks > 0 && (
                                <div className="mt-3">
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>Queue Progress</span>
                                    <span>{Math.round((queue.completed_tasks / (queue.completed_tasks + queue.pending_tasks + queue.active_tasks)) * 100)}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                      style={{ 
                                        width: `${Math.round((queue.completed_tasks / (queue.completed_tasks + queue.pending_tasks + queue.active_tasks)) * 100)}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Server className="w-5 h-5 text-indigo-600" />
                          Active Task Queues
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Default Queues when no data */}
                          <div className="bg-gradient-to-br from-white to-gray-50 border rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                Merchant Processing
                              </h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Completed Tasks:</span>
                                <span className="font-semibold text-green-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gradient-to-br from-white to-gray-50 border rounded-lg p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                MCC Classification
                              </h4>
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                Active
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Active Tasks:</span>
                                <span className="font-semibold text-blue-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pending Tasks:</span>
                                <span className="font-semibold text-yellow-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Completed Tasks:</span>
                                <span className="font-semibold text-green-600">0</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avg Processing Time:</span>
                                <span className="font-semibold">N/A</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Real-time Workflow Activity Feed */}
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-green-600" />
                        Real-time Workflow Activity
                      </h3>
                      {temporalData.recent_workflows && temporalData.recent_workflows.length > 0 ? (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {temporalData.recent_workflows.map((workflow, index) => (
                            <div key={index} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full ${
                                    workflow.status === 'completed' ? 'bg-green-500' :
                                    workflow.status === 'running' ? 'bg-blue-500' :
                                    workflow.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                                  }`}></div>
                                  <div>
                                    <div className="font-semibold text-sm">{workflow.workflow_id}</div>
                                    <div className="text-xs text-gray-600">{workflow.workflow_type}</div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(workflow.status)}`}>
                                    {workflow.status}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(workflow.start_time).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              {workflow.duration && (
                                <div className="mt-2 text-xs text-gray-500">
                                  Duration: {workflow.duration}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                          <RefreshCw className="w-12 h-12 mx-auto mb-2 text-gray-400 animate-spin" />
                          <p>No recent workflow activity</p>
                          <p className="text-sm">Real-time updates will appear here when Temporal workflows are active</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <RefreshCw className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading Temporal queue status...</p>
                    <p className="text-sm text-gray-500 mt-2">Connecting to Temporal server for real-time monitoring</p>
                  </div>
                )}

                {/* Enhanced Temporal Server Status */}
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-purple-900">Temporal Server Connection</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Real-time workflow orchestration and queue monitoring
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded text-sm font-semibold ${
                        temporalData?.server_status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {temporalData?.server_status === 'connected' ? '🟢 Connected' : '🟡 Connecting...'}
                      </span>
                      <button
                        onClick={fetchAllData}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm flex items-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-purple-700">Server:</span>
                      <span className="font-mono text-xs">{temporalData?.server_url || 'localhost:7233'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">Namespace:</span>
                      <span className="font-mono text-xs">{temporalData?.namespace || 'default'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-700">Queues:</span>
                      <span className="font-semibold text-purple-900">{temporalData?.queues?.length || 0}</span>
                    </div>
                    {temporalData?.last_updated && (
                      <div className="flex justify-between">
                        <span className="text-purple-700">Updated:</span>
                        <span className="font-mono text-xs">{new Date(temporalData.last_updated).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'docker' && dockerData && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Server className="w-6 h-6 text-blue-600" />
                  Docker Containers
                </h2>
                <button
                  onClick={fetchAllData}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="divide-y">
              {dockerData.containers
                ?.filter((container) => !isOneOffContainer(container.name))
                .map((container) => (
                  <div key={container.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{container.name}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(container.status)}`}>
                            {container.status}
                          </span>
                          {container.health !== 'unknown' && (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(container.health)}`}>
                              {container.health}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Image: </span>
                            <span className="font-medium">{container.image}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">ID: </span>
                            <span className="font-mono text-xs">{container.id}</span>
                          </div>
                        </div>
                      </div>
                      {container.status === 'running' && (
                        <button
                          onClick={() => restartContainer(container.name)}
                          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
                        >
                          Restart
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Vulnerabilities Tab */}
        {activeTab === 'vulnerabilities' && vulnerabilities && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">Critical</div>
                <div className="text-3xl font-bold text-red-600">
                  {vulnerabilities.total_critical}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">High</div>
                <div className="text-3xl font-bold text-orange-600">
                  {vulnerabilities.total_high}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">Medium</div>
                <div className="text-3xl font-bold text-yellow-600">
                  {vulnerabilities.total_medium}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 mb-2">Low</div>
                <div className="text-3xl font-bold text-blue-600">
                  {vulnerabilities.total_low}
                </div>
              </div>
            </div>

            {/* Python Vulnerabilities */}
            {vulnerabilities.python_packages?.vulnerabilities?.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b">
                  <h2 className="text-xl font-bold">Python Package Vulnerabilities</h2>
                </div>
                <div className="divide-y max-h-96 overflow-y-auto">
                  {vulnerabilities.python_packages.vulnerabilities.map((vuln, idx) => (
                    <div key={idx} className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">{vuln.package}</div>
                          <div className="text-sm text-gray-600 mt-1">{vuln.id}</div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(vuln.severity?.toLowerCase())}`}>
                          {vuln.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vulnerabilities.python_packages?.status === 'tool_not_installed' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  {vulnerabilities.python_packages.message}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Versions Tab */}
        {activeTab === 'versions' && versions && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Package className="w-6 h-6 text-indigo-600" />
                  Component Versions
                </h2>
              </div>
              <div className="divide-y">
                {Object.entries(versions).map(([component, info]) => (
                  <div key={component} className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold capitalize">{component}</h3>
                        {info.current && (
                          <p className="text-sm text-gray-600 mt-1">
                            Current: <span className="font-mono">{info.current}</span>
                          </p>
                        )}
                        {info.error && (
                          <p className="text-sm text-red-600 mt-1">Error: {info.error}</p>
                        )}
                      </div>
                      {component === 'github' && info.latest_release && (
                        <a
                          href={info.latest_release.html_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Latest: {info.latest_release.tag_name}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default SystemHealthPage;
